import { IconInfoCircle } from "@tabler/icons-react"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Label } from "design-system/atoms/Label"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { renderLiquid } from "lopermut"
import { Braces, PencilIcon } from "lucide-react"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { useVaultPreviewContext } from "modules/shared/hooks/useVaultPreviewContext"
import { useEffect, useMemo, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD, CAMPAIGN_API_CALLBACK_SAMPLE_DATA } from "schema"

type Props = {
  value: string
  onChange: (payload: string) => void
  disabled?: boolean
}

function renderPreview(template: string, context: Record<string, unknown>) {
  try {
    const rendered = renderLiquid(
      template.trim() === "" ? CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD : template.trim(),
      context,
      { jsonSafe: true },
    )
    if (rendered) {
      JSON.parse(rendered)
      return rendered
    }
  } catch {
    return null
  }
}

export function EditApiCallbackPayloadDialog({ value, onChange, disabled = false }: Props) {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [payload, setPayload] = useState(value)
  const previewContext = useVaultPreviewContext(CAMPAIGN_API_CALLBACK_SAMPLE_DATA)

  useEffect(() => {
    if (isOpen) setPayload(value)
  }, [isOpen, value])

  const renderedPayload = useMemo(
    () => renderPreview(payload, previewContext),
    [payload, previewContext],
  )
  const isValidPayload = !!renderedPayload
  const isDirty = value !== payload

  const lastValidPayloadRef = useRef(renderedPayload)
  if (isValidPayload) lastValidPayloadRef.current = renderedPayload

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-fit" disabled={disabled}>
          <PencilIcon className="size-4" />
          {t("campaigns.channel.api.payload.edit")}
        </Button>
      </DialogTrigger>
      <DialogContent className="flex max-h-[90dvh] min-h-0 w-full flex-col overflow-hidden sm:max-w-4xl sm:w-[calc(100vw-2rem)]">
        <DialogHeader className="shrink-0">
          <DialogTitle>{t("campaigns.channel.api.payload.edit")}</DialogTitle>
          <DialogDescription className="flex flex-col gap-2 items-start">
            <span>
              <Trans
                i18nKey="campaigns.channel.api.payload.description"
                components={{
                  b: <span className="font-medium" />,
                  a: (
                    <a
                      href="https://shopify.dev/docs/api/liquid"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:opacity-80"
                    />
                  ),
                }}
              />
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden md:flex-row">
          <div className="flex min-h-0 flex-1 min-w-0 flex-col gap-4 overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden px-1.5">
              <Label className="shrink-0">{t("campaigns.channel.api.payload.template")}</Label>
              <VaultVariableAutocomplete variables={[]} className="flex min-h-0 flex-1">
                <textarea
                  className="min-h-0 w-full flex-1 resize-none overflow-y-auto rounded-md border border-input bg-background px-3 py-2 font-mono text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  style={{ tabSize: 2 }}
                  value={payload}
                  placeholder={CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.stopPropagation()
                  }}
                  onChange={(e) => setPayload(e.target.value)}
                />
              </VaultVariableAutocomplete>
              <Button
                variant="outline"
                size="sm"
                className="w-fit shrink-0"
                onClick={() => setPayload(CAMPAIGN_API_CALLBACK_DEFAULT_PAYLOAD)}
              >
                <Braces className="size-4" />
                {t("campaigns.channel.api.payload.useDefault")}
              </Button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden">
              <Label className="flex shrink-0 items-center gap-1">
                <span>{t("campaigns.channel.api.payload.data")}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconInfoCircle className="size-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t("campaigns.channel.api.payload.dataTooltip")}</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
              <MarkdownRenderer
                className="flex min-h-0 flex-1 flex-col overflow-auto rounded-md border border-border"
                cardClassName="border-none shadow-none"
                codeClassName="border-none"
              >
                {`\`\`\`json\n${JSON.stringify(CAMPAIGN_API_CALLBACK_SAMPLE_DATA, null, 2)}\n\`\`\``}
              </MarkdownRenderer>
            </div>
          </div>
          <div className="flex min-h-0 flex-1 min-w-0 flex-col gap-2">
            <Label className="shrink-0">{t("campaigns.channel.api.payload.preview")}</Label>
            <MarkdownRenderer
              className="flex min-h-0 flex-1 flex-col overflow-auto rounded-md border border-border"
              cardClassName="border-none shadow-none"
              codeClassName="border-none"
            >
              {`\`\`\`json
${
  lastValidPayloadRef.current
    ? JSON.stringify(JSON.parse(lastValidPayloadRef.current), null, 2)
    : t("campaigns.channel.api.payload.invalidTemplate")
}
\`\`\``}
            </MarkdownRenderer>
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {t("shared.cancel")}
          </Button>
          <Button
            onClick={() => {
              onChange(payload)
              setIsOpen(false)
            }}
            disabled={!isValidPayload || !isDirty}
          >
            {t("shared.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
